<template>
<div class="voice-recorder">
<!-- Compact mode when no recording exists -->
<div v-if="!expandedMode && !recordingExists" class="compact-recorder">
  <button
    class="expand-button"
    @click="expandRecorder"
    :style="{ borderColor: speakerColor }"
  >
    <span class="expand-icon">🎙️</span>
    <span class="expand-text">Add Custom Voice Recording</span>
    <span class="expand-arrow">›</span>
  </button>
</div>

<!-- Existing Recording Info (always visible when recording exists and not expanded) -->
<div v-if="recordingExists && !expandedMode && !isRecording" class="existing-recording">
  <div class="existing-recording-info">
    <span class="recording-status">🎙️ Custom voice recording active</span>
    <span class="recording-details">{{ recordingDuration }}s • {{ recordingSize }} • {{ recordingFormat }}</span>
  </div>
  <div class="existing-recording-actions">
    <button class="action-button preview-button" @click="playPreview" :disabled="isPreviewPlaying">
      {{ isPreviewPlaying ? '⏸ Pause' : '▶ Play' }}
    </button>
    <button class="action-button edit-button" @click="expandRecorder">
      ✏️ Edit Recording
    </button>
    <button class="action-button delete-button" @click="deleteRecording">
      🗑️ Delete
    </button>
  </div>
</div>

<!-- Expanded mode content -->
<div v-if="expandedMode || isRecording" class="expanded-content">
  <!-- Show existing recording info when expanded -->
  <div v-if="recordingExists && !isRecording && expandedMode" class="existing-recording expanded">
    <div class="existing-recording-info">
      <span class="recording-status">🎙️ Current Recording</span>
      <span class="recording-details">{{ recordingDuration }}s • {{ recordingSize }} • {{ recordingFormat }}</span>
    </div>
    <div class="existing-recording-actions">
      <button class="action-button preview-button" @click="playPreview" :disabled="isPreviewPlaying">
        {{ isPreviewPlaying ? '⏸ Pause' : '▶ Play' }}
      </button>
      <button class="action-button delete-button" @click="deleteRecording">
        🗑️ Delete
      </button>
    </div>
  </div>

  <!-- Audio Level Indicator -->
  <div v-if="showAudioLevel && !isRecording" class="audio-level-container">
    <div class="audio-level-bar" :style="{ width: audioLevel + '%', backgroundColor: speakerColor }"></div>
    <span class="audio-level-text">{{ audioLevel > 30 ? 'Good' : 'Too quiet' }}</span>
  </div>

  <!-- Recording Tips -->
  <div v-if="!isRecording && showTips && !recordingExists" class="recording-tips">
    <h4>Recording Tips:</h4>
    <ul>
      <li>Use a quiet environment with minimal background noise</li>
      <li>Keep your microphone 6-12 inches from your mouth</li>
      <li>Speak clearly and at a consistent volume</li>
      <li>Use headphones to prevent echo</li>
      <li>Use a high-quality microphone if available</li>
    </ul>
  </div>

  <!-- Quality Settings -->
  <div v-if="!isRecording" class="quality-settings">
    <select v-model="selectedQuality" class="quality-select">
      <option value="studio">Studio Quality (384 kbps)</option>
      <option value="high">High Quality (256 kbps)</option>
      <option value="standard">Standard Quality (128 kbps)</option>
    </select>
  </div>

  <div class="recorder-controls">
    <button
      class="record-button"
      :class="{ 'is-recording': isRecording, 'is-preparing': isPreparingRecording }"
      @click="toggleRecording"
      :disabled="isProcessing || isPreparingRecording"
      :style="{ backgroundColor: isRecording ? speakerColor : '#e53e3e' }"
    >
      <div class="button-content" :title="recordingExists ? 'Record a new voice clip to replace the existing one' : 'Recording your voice will replace the default AI voice for this segment. When playing the content, users will hear your recorded voice instead.'">
        <i class="record-icon" :class="{ 'is-recording': isRecording }">
          {{ isRecording ? '⏹' : isPreparingRecording ? '⏳' : '🎙️' }}
        </i>
        <span class="button-text">
          {{ getButtonText() }}
        </span>
      </div>
    </button>

    <!-- Cancel button during expanded mode with existing recording -->
    <button v-if="expandedMode && recordingExists && !isRecording && !hasNewRecording"
      class="cancel-button"
      @click="collapseRecorder">
      Cancel
    </button>
  </div>

  <!-- Actions for newly recorded audio -->
  <div v-if="hasNewRecording && !isRecording" class="recording-actions">
    <button class="action-button preview-button large" @click="playPreview" :disabled="isPreviewPlaying">
      {{ isPreviewPlaying ? '⏸ Pause' : '▶ Play Preview' }}
    </button>
    <button class="action-button save-button large" @click="saveRecording" :disabled="isProcessing">
      💾 Save Recording
    </button>
    <button class="action-button discard-button large" @click="discardNewRecording">
      ❌ Discard
    </button>
  </div>

  <div v-if="recordingExists && isRecording" class="recording-warning">
    ⚠️ Recording will replace existing audio
  </div>
</div>

<!-- Countdown Timer -->
<div v-if="showCountdown && !isRecording" class="countdown-overlay">
  <div class="countdown-content">
    <div class="countdown-number">{{ countdownNumber }}</div>
    <div class="countdown-text">Get ready to record!</div>
    <div class="countdown-cancel" @click="cancelCountdown">Cancel</div>
  </div>
</div>

<div v-if="isRecording" class="recording-indicator">
  <div class="record-wave">
    <div v-for="n in 5" :key="n" class="wave-bar" :style="{ backgroundColor: speakerColor }"></div>
  </div>
  <span class="recording-timer" :style="{ color: speakerColor }">{{ formattedTime }}</span>
  <div class="recording-quality">{{ currentQualityLabel }}</div>
</div>

<div v-if="hasNewRecording && !isRecording" class="recording-info">
  <span class="recording-length">{{ recordingDuration }}s</span>
  <span class="recording-size">{{ recordingSize }}</span>
  <span class="recording-format">{{ recordingFormat }}</span>
  <div class="recording-waveform">
    <div v-for="n in 40" :key="n"
      class="waveform-bar"
      :style="{
        height: getWaveformHeight(n) + 'px',
        backgroundColor: speakerColor
      }"
    ></div>
  </div>
</div>
</div>
</template>

<script>
import API from '@/api';
import { mapActions } from 'vuex';

export default {
name: 'VoiceRecorder',
props: {
segmentIndex: {
  type: Number,
  required: true
},
isPro: {
  type: Boolean,
  default: false
},
segment: {
  type: Object,
  required: true
},
messageId: {
  required: true
},
speakerColor: {
  type: String,
  default: '#e53e3e'
},
showTips: {
  type: Boolean,
  default: true
},
customRecordings: {
  type: Map,
  default: () => new Map()
}
},
data() {
return {
  // Recording state
  isRecording: false,
  isProcessing: false,
  isPreparingRecording: false,
  recordingExists: this.checkExistingRecording(),
  isPreviewPlaying: false,
  mediaRecorder: null,
  audioChunks: [],
  audioBlob: null,
  audioUrl: null,
  previewAudio: null,
  startTime: null,
  recordingTime: 0,
  recordingDuration: 0,
  timerInterval: null,
  recordingSize: '0 KB',
  recordingFormat: '',

  // Audio quality settings
  selectedQuality: 'studio',
  qualitySettings: {
    studio: { bitrate: 384000, sampleRate: 48000, label: 'Studio Quality' },
    high: { bitrate: 256000, sampleRate: 48000, label: 'High Quality' },
    standard: { bitrate: 128000, sampleRate: 44100, label: 'Standard Quality' }
  },

  // Audio level monitoring
  showAudioLevel: false,
  audioLevel: 0,
  audioContext: null,
  analyser: null,
  microphone: null,
  javascriptNode: null,

  // Countdown
  showCountdown: false,
  countdownNumber: 3,
  countdownTimer: null,

  // UI state
  expandedMode: false,
  savedToServer: false,
  hasNewRecording: false,

  // Audio Worklet
  audioWorklet: null,
  audioWorkletNode: null,

  // Supported audio formats in order of preference
  supportedFormats: [
    { mimeType: 'audio/webm;codecs=opus', extension: 'webm', quality: 'excellent' },
    { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg', quality: 'excellent' },
    { mimeType: 'audio/mp4;codecs=aac', extension: 'mp4', quality: 'good' },
    { mimeType: 'audio/webm', extension: 'webm', quality: 'good' },
    { mimeType: 'audio/mpeg', extension: 'mp3', quality: 'good' }
  ],

  // Selected recording format
  recordingOptions: {
    mimeType: null,
    audioBitsPerSecond: null
  },

  // Waveform data
  waveformData: []
}
},
computed: {
formattedTime() {
  const minutes = Math.floor(this.recordingTime / 60);
  const seconds = this.recordingTime % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
},
currentQualityLabel() {
  return this.qualitySettings[this.selectedQuality].label;
}
},
watch: {
segment: {
  handler(newSegment) {
    // Update recording state when segment changes
    if (newSegment && newSegment.customVoiceUrl) {
      if (!this.recordingExists) {
        this.recordingExists = true;
        this.savedToServer = true;
        this.recordingDuration = newSegment.customVoiceDuration || 0;
        this.recordingSize = newSegment.customVoiceSize || 'Unknown';
        this.recordingFormat = newSegment.customVoiceFormat || 'Audio';
      }
    }
  },
  deep: true,
  immediate: true
},
customRecordings: {
  handler(newCustomRecordings) {
    // Check if this segment has a custom recording
    const hasRecording = newCustomRecordings.has(this.segmentIndex);
    if (hasRecording && !this.recordingExists) {
      this.recordingExists = true;
      this.savedToServer = true;
      // Update recording details if available
      const recordingData = newCustomRecordings.get(this.segmentIndex);
      if (recordingData) {
        this.recordingDuration = recordingData.duration || 0;
        this.recordingSize = recordingData.size || 'Unknown';
        this.recordingFormat = recordingData.format || 'Audio';

        // Set up preview audio with the server URL
        if (recordingData.audioUrl) {
          this.setupPreviewAudio(recordingData.audioUrl);
        }
      }
    } else if (!hasRecording && this.recordingExists && !this.hasNewRecording) {
      // Recording was removed from parent
      this.recordingExists = false;
      this.savedToServer = false;
      if (this.previewAudio) {
        this.previewAudio.pause();
        this.previewAudio = null;
      }
    }
  },
  deep: true,
  immediate: true
}
},
methods: {
...mapActions('websocket', ['showTempMessageFn']),

checkExistingRecording() {
  // Check if custom recording exists in the customRecordings Map
  return this.customRecordings.has(this.segmentIndex) || !!(this.segment && this.segment.customVoiceUrl);
},

getButtonText() {
  if (this.isPreparingRecording) return 'Starting...';
  if (this.isRecording) return 'Stop Recording';
  if (this.isProcessing) return 'Processing...';
  if (this.recordingExists) return 'Record New Voice';
  return 'Record Your Voice';
},

async toggleRecording() {
  if (this.isRecording) {
    this.stopRecording();
  } else {
    await this.startRecordingWithCountdown();
  }
},

async startRecordingWithCountdown() {
  this.isPreparingRecording = true;
  this.showCountdown = true;
  this.countdownNumber = 3;

  // Initialize audio format
  this.initializeRecordingFormat();

  // Start countdown
  this.countdownTimer = setInterval(() => {
    this.countdownNumber--;
    if (this.countdownNumber === 0) {
      clearInterval(this.countdownTimer);
      setTimeout(() => {
        this.showCountdown = false;
        this.startRecording();
      }, 100);
    }
  }, 1000);
},

cancelCountdown() {
  clearInterval(this.countdownTimer);
  this.showCountdown = false;
  this.isPreparingRecording = false;
  this.countdownNumber = 3;
},

initializeRecordingFormat() {
  // Find the best supported format
  for (const format of this.supportedFormats) {
    if (MediaRecorder.isTypeSupported(format.mimeType)) {
      this.recordingOptions.mimeType = format.mimeType;
      this.recordingFormat = format.extension.toUpperCase();
      break;
    }
  }

  // Set bitrate based on quality selection
  const quality = this.qualitySettings[this.selectedQuality];
  this.recordingOptions.audioBitsPerSecond = quality.bitrate;
},

async startRecording() {
  try {
    this.isPreparingRecording = false;

    // Reset state
    this.audioChunks = [];
    this.recordingTime = 0;
    this.waveformData = [];

    // Get quality settings
    const quality = this.qualitySettings[this.selectedQuality];

    // Request microphone access with optimized settings
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,  // Disable for better quality
        noiseSuppression: false,  // Disable for better quality
        autoGainControl: false,   // Disable for better quality
        sampleRate: quality.sampleRate,
        sampleSize: 24,          // 24-bit audio for better quality
        channelCount: 1,         // Mono for voice
        latency: 0,              // Lowest latency
        googEchoCancellation: false,
        googAutoGainControl: false,
        googNoiseSuppression: false,
        googHighpassFilter: false,
        googTypingNoiseDetection: false,
        googAudioMirroring: false
      }
    });

    // Set up audio level monitoring
    this.setupAudioLevelMonitoring(stream);

    // Create media recorder with selected format
    this.mediaRecorder = new MediaRecorder(stream, this.recordingOptions);

    // Set up event handlers
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
        this.generateWaveformData(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      this.processRecording(stream);
    };

    // Start recording with smaller timeslice for better real-time processing
    this.mediaRecorder.start(100); // Collect data every 100ms
    this.isRecording = true;
    this.startTime = Date.now();

    // Start timer
    this.timerInterval = setInterval(() => {
      this.recordingTime = Math.floor((Date.now() - this.startTime) / 1000);
    }, 1000);
  } catch (error) {
    console.error('Error starting recording:', error);
    alert('Failed to access microphone. Please check your permissions and microphone connection.');
    this.isRecording = false;
    this.isPreparingRecording = false;
    this.showCountdown = false;
  }
},

expandRecorder() {
  this.expandedMode = true;
  // Initialize audio level monitoring when expanded
  this.initializeAudioContext();
},

collapseRecorder() {
  this.expandedMode = false;
  // Clean up any unsaved recordings
  if (this.hasNewRecording && !this.savedToServer) {
    this.discardNewRecording();
  }
},

async initializeAudioContext() {
  try {
    // Check if browser supports getUserMedia
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.setupAudioLevelMonitoring(stream);
    // Stop the stream after setup
    stream.getTracks().forEach(track => track.stop());
  } catch (error) {
    console.warn('Could not initialize audio monitoring:', error);
  }
},

async setupAudioLevelMonitoring(stream) {
  try {
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Modern approach using AudioWorklet (if available)
    if (this.audioContext.audioWorklet) {
      // Create an inline worklet processor
      const processorCode = `
        class VolumeProcessor extends AudioWorkletProcessor {
          constructor() {
            super();
            this.volume = 0;
            this.updateInterval = 10;
            this.nextUpdateFrame = this.updateInterval;
          }

          process(inputs, outputs, parameters) {
            const input = inputs[0];
            if (input.length > 0) {
              const samples = input[0];
              let sum = 0;

              // Calculate RMS
              for (let i = 0; i < samples.length; ++i) {
                sum += samples[i] * samples[i];
              }

              const rms = Math.sqrt(sum / samples.length);
              this.volume = Math.max(rms, this.volume * 0.95);

              // Send volume updates
              if (--this.nextUpdateFrame <= 0) {
                this.nextUpdateFrame = this.updateInterval;
                this.port.postMessage({ volume: this.volume });
              }
            }
            return true;
          }
        }
        registerProcessor('volume-processor', VolumeProcessor);
      `;

      const blob = new Blob([processorCode], { type: 'application/javascript' });
      const processorUrl = URL.createObjectURL(blob);

      await this.audioContext.audioWorklet.addModule(processorUrl);

      this.microphone = this.audioContext.createMediaStreamSource(stream);
      this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'volume-processor');

      this.audioWorkletNode.port.onmessage = (event) => {
        this.audioLevel = Math.min(100, Math.round(event.data.volume * 300));
      };

      this.microphone.connect(this.audioWorkletNode);
      this.audioWorkletNode.connect(this.audioContext.destination);

      URL.revokeObjectURL(processorUrl);
    } else {
      // Fallback to ScriptProcessorNode for older browsers
      this.analyser = this.audioContext.createAnalyser();
      this.microphone = this.audioContext.createMediaStreamSource(stream);
      this.javascriptNode = this.audioContext.createScriptProcessor(2048, 1, 1);

      this.analyser.smoothingTimeConstant = 0.8;
      this.analyser.fftSize = 1024;

      this.microphone.connect(this.analyser);
      this.analyser.connect(this.javascriptNode);
      this.javascriptNode.connect(this.audioContext.destination);

      this.javascriptNode.onaudioprocess = () => {
        const array = new Uint8Array(this.analyser.frequencyBinCount);
        this.analyser.getByteFrequencyData(array);

        // Calculate average volume
        const average = array.reduce((a, b) => a + b) / array.length;
        this.audioLevel = Math.min(100, Math.round(average));
      };
    }

    this.showAudioLevel = true;
  } catch (error) {
    console.error('Error setting up audio monitoring:', error);
    // Continue without audio level monitoring
  }
},

processRecording(stream) {
  // Create a blob from the recorded chunks
  this.audioBlob = new Blob(this.audioChunks, { type: this.recordingOptions.mimeType });

  // Calculate size
  this.recordingSize = this.formatBytes(this.audioBlob.size);

  // Create a URL for the blob
  if (this.audioUrl) {
    URL.revokeObjectURL(this.audioUrl);
  }
  this.audioUrl = URL.createObjectURL(this.audioBlob);

  // Set up preview audio with local blob URL for new recordings
  this.setupPreviewAudio(this.audioUrl);

  // Update state
  this.hasNewRecording = true;
  this.recordingDuration = Math.round(this.recordingTime);
  this.isRecording = false;
  this.isProcessing = false;

  // Stop all tracks in the stream
  stream.getTracks().forEach(track => track.stop());

  // Clean up audio monitoring
  this.cleanupAudioMonitoring();
},

cleanupAudioMonitoring() {
  if (this.audioWorkletNode) {
    this.audioWorkletNode.disconnect();
    this.audioWorkletNode = null;
  }

  if (this.javascriptNode) {
    this.javascriptNode.disconnect();
  }

  if (this.microphone) {
    this.microphone.disconnect();
  }

  if (this.analyser) {
    this.analyser.disconnect();
  }

  if (this.audioContext) {
    this.audioContext.close();
  }

  this.showAudioLevel = false;
},

stopRecording() {
  if (this.mediaRecorder && this.isRecording) {
    this.mediaRecorder.stop();
    clearInterval(this.timerInterval);
    this.isProcessing = true;
  }
},

async setupPreviewAudio(url) {
  // Clean up existing audio if any
  if (this.previewAudio) {
    this.previewAudio.pause();
    this.previewAudio = null;
  }

  try {
    let audioUrl = url;

    // If this is a proxy URL, fetch the actual audio data (like ConversationPlayer does)
    if (url.includes('/voice-proxy')) {

      const response = await API.get(url, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json'
        }
      });

      // Create a blob from the audio data
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      audioUrl = URL.createObjectURL(audioBlob);
    }

    // Create new audio element
    this.previewAudio = new Audio(audioUrl);

    this.previewAudio.onended = () => {
      this.isPreviewPlaying = false;
    };

    this.previewAudio.onerror = (error) => {
      console.error('Error loading preview audio:', error);
      this.isPreviewPlaying = false;
    };

  } catch (error) {
    console.error('Error setting up preview audio:', error);
    this.showTempMessageFn({
      message: 'Failed to load audio for preview',
      type: 'error'
    });
  }
},

playPreview() {
  if (!this.previewAudio) {
    // If no preview audio is set up, try to set it up from customRecordings
    const recordingData = this.customRecordings.get(this.segmentIndex);
    if (recordingData && recordingData.audioUrl) {
      this.setupPreviewAudio(recordingData.audioUrl);
    } else {
      console.warn('No audio URL available for preview');
      return;
    }
  }

  if (this.isPreviewPlaying) {
    this.previewAudio.pause();
    this.isPreviewPlaying = false;
  } else {
    this.previewAudio.currentTime = 0;
    this.previewAudio.play()
      .then(() => {
        this.isPreviewPlaying = true;
      })
      .catch(error => {
        console.error('Error playing audio:', error);
        this.isPreviewPlaying = false;
      });
  }
},

async saveRecording() {
  if (this.audioBlob) {
    this.isProcessing = true;

    try {
      const formData = new FormData();

      // Generate filename based on format
      const filename = `recording.${this.recordingFormat.toLowerCase()}`;

      formData.append('file', this.audioBlob, filename);
      formData.append('duration', this.recordingDuration.toString());
      formData.append('format', this.recordingOptions.mimeType);
      formData.append('quality', this.selectedQuality);
      formData.append('bitrate', this.recordingOptions.audioBitsPerSecond.toString());

      // Upload to the server
      const response = await API.post(
        `/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}/voice`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Extract the server-assigned URL
      const serverUrl = response.data.url;

      // Update preview audio to use server URL instead of blob URL
      this.setupPreviewAudio(serverUrl);

      // Mark as saved to server
      this.savedToServer = true;
      this.recordingExists = true;
      this.hasNewRecording = false;
      this.expandedMode = false; // Collapse after saving

      // Emit event with recording data
      this.$emit('recording-saved', {
        segmentIndex: this.segmentIndex,
        audioBlob: this.audioBlob,
        audioUrl: serverUrl,
        duration: this.recordingDuration,
        format: this.recordingFormat,
        quality: this.selectedQuality,
        size: this.recordingSize
      });

      this.showTempMessageFn({
        message: 'Recording saved successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving recording to server:', error);
      alert('Failed to save recording. Please try again.');
    } finally {
      this.isProcessing = false;
    }
  }
},

discardNewRecording() {
  if (this.previewAudio) {
    this.previewAudio.pause();
  }

  if (this.audioUrl) {
    URL.revokeObjectURL(this.audioUrl);
  }

  // Reset state for new recording
  this.audioChunks = [];
  this.audioBlob = null;
  this.audioUrl = null;
  this.previewAudio = null;
  this.hasNewRecording = false;
  this.isPreviewPlaying = false;
  this.waveformData = [];
  this.recordingSize = '0 KB';
  this.recordingDuration = 0;

  // If no existing recording, collapse
  if (!this.recordingExists) {
    this.expandedMode = false;
  }
},

async deleteRecording() {
  if (this.previewAudio) {
    this.previewAudio.pause();
  }

  // Clean up blob URLs to prevent memory leaks
  if (this.audioUrl && this.audioUrl.startsWith('blob:')) {
    URL.revokeObjectURL(this.audioUrl);
  }

  // Also clean up the preview audio source if it's a blob URL
  if (this.previewAudio && this.previewAudio.src.startsWith('blob:')) {
    URL.revokeObjectURL(this.previewAudio.src);
  }

  // Check if we need to delete from server
  if (this.savedToServer || (this.segment && this.segment.customVoiceUrl)) {
    this.isProcessing = true;

    try {
      await API.delete(
        `/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}/voice`
      );

      this.showTempMessageFn({
        message: 'Recording deleted',
        type: 'info'
      });
    } catch (error) {
      console.error('Error deleting recording from server:', error);
      alert('Failed to delete recording. Please try again.');
      this.isProcessing = false;
      return;
    } finally {
      this.isProcessing = false;
    }
  }

  // Reset all state
  this.audioChunks = [];
  this.audioBlob = null;
  this.audioUrl = null;
  this.previewAudio = null;
  this.recordingExists = false;
  this.hasNewRecording = false;
  this.isPreviewPlaying = false;
  this.waveformData = [];
  this.recordingSize = '0 KB';
  this.recordingFormat = '';
  this.savedToServer = false;
  this.expandedMode = false;

  // Emit event
  this.$emit('recording-deleted', this.segmentIndex);
},

generateWaveformData(dataChunk) {
  // Simple waveform generation - in production you might want to use Web Audio API
  if (dataChunk.size > 0) {
    const height = Math.floor(Math.random() * 20) + 5;
    this.waveformData.push(height);
    if (this.waveformData.length > 40) {
      this.waveformData.shift();
    }
  }
},

getWaveformHeight(index) {
  return this.waveformData[index - 1] || Math.floor(Math.random() * 15) + 5;
},

formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
},

handleKeyboardShortcuts(e) {
  // Recording shortcuts
  if (this.isRecording) {
    switch (e.key) {
      case 'Escape':
      case 'q':
      case 'Q':
        e.preventDefault();
        this.stopRecording();
        break;
    }
  } else if (!this.isRecording && !this.hasNewRecording && this.expandedMode) {
    switch (e.key) {
      case 'r':
      case 'R':
        e.preventDefault();
        this.startRecordingWithCountdown();
        break;
    }
  }
}
},
mounted() {
// Initialize recording format
this.initializeRecordingFormat();

// Add keyboard shortcuts
window.addEventListener('keydown', this.handleKeyboardShortcuts);

// Check if recording exists initially and set properties
if (this.checkExistingRecording()) {
  this.recordingExists = true;
  this.savedToServer = true;

  // Try to get recording details from customRecordings Map first
  const recordingData = this.customRecordings.get(this.segmentIndex);
  if (recordingData) {
    this.recordingDuration = recordingData.duration || 0;
    this.recordingSize = recordingData.size || 'Unknown';
    this.recordingFormat = recordingData.format || 'Audio';

    // Set up preview audio with the server URL (async)
    if (recordingData.audioUrl) {
      this.setupPreviewAudio(recordingData.audioUrl).catch(error => {
        console.error('Failed to setup preview audio on mount:', error);
      });
    }
  } else if (this.segment) {
    // Fallback to segment data
    this.recordingDuration = this.segment.customVoiceDuration || 0;
    this.recordingSize = this.segment.customVoiceSize || 'Unknown';
    this.recordingFormat = this.segment.customVoiceFormat || 'Audio';
  }
}
},
beforeDestroy() {
// Clean up
if (this.previewAudio) {
  this.previewAudio.pause();
  // Clean up blob URL if it exists
  if (this.previewAudio.src.startsWith('blob:')) {
    URL.revokeObjectURL(this.previewAudio.src);
  }
}

if (this.audioUrl && this.audioUrl.startsWith('blob:')) {
  URL.revokeObjectURL(this.audioUrl);
}

clearInterval(this.timerInterval);
clearInterval(this.countdownTimer);
this.cleanupAudioMonitoring();

// Remove keyboard shortcuts
window.removeEventListener('keydown', this.handleKeyboardShortcuts);
}
}
</script>

<style scoped>
/* Compact Mode */
.compact-recorder {
margin-top: 0.5rem;
}

.expand-button {
width: 100%;
padding: 0.75rem 1rem;
background-color: transparent;
border: 1px solid rgba(255, 255, 255, 0.2);
border-radius: 0.5rem;
color: rgba(255, 255, 255, 0.9);
cursor: pointer;
display: flex;
align-items: center;
justify-content: space-between;
transition: all 0.2s ease;
font-size: 0.9375rem;
}

.expand-button:hover {
background-color: rgba(255, 255, 255, 0.05);
border-color: rgba(255, 255, 255, 0.3);
color: white;
}

.expand-icon {
font-size: 1.25rem;
margin-right: 0.75rem;
}

.expand-text {
flex: 1;
text-align: left;
}

.expand-arrow {
font-size: 1.375rem;
transition: transform 0.2s ease;
}

.expand-button:hover .expand-arrow {
transform: translateX(3px);
}

/* Expanded Content */
.expanded-content {
margin-top: 0.75rem;
animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
from { opacity: 0; transform: translateY(-10px); }
to { opacity: 1; transform: translateY(0); }
}

/* Voice Recorder Container */
.voice-recorder {
margin-top: 0.5rem;
padding: 0.75rem;
border-radius: 0.625rem;
background-color: rgba(255, 255, 255, 0.05);
backdrop-filter: blur(10px);
border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Existing Recording */
.existing-recording {
margin-bottom: 1rem;
padding: 0.875rem;
background-color: rgba(255, 255, 255, 0.06);
border: 1px solid rgba(255, 255, 255, 0.15);
border-radius: 0.5rem;
}

.existing-recording.expanded {
margin-bottom: 1.25rem;
}

.existing-recording-info {
display: flex;
flex-direction: column;
gap: 0.375rem;
margin-bottom: 0.875rem;
}

.recording-status {
font-weight: 600;
color: #48bb78;
font-size: 0.9375rem;
}

.recording-details {
font-size: 0.875rem;
color: rgba(255, 255, 255, 0.75);
}

.existing-recording-actions {
display: flex;
gap: 0.625rem;
flex-wrap: wrap;
}

.existing-recording-actions .action-button {
flex: 1;
min-width: 90px;
padding: 0.625rem 1rem;
height: auto;
font-size: 0.875rem;
}

/* Audio Level Indicator */
.audio-level-container {
margin-bottom: 1rem;
height: 24px;
background-color: rgba(255, 255, 255, 0.1);
border-radius: 12px;
overflow: hidden;
position: relative;
}

.audio-level-bar {
height: 100%;
transition: width 0.1s ease;
border-radius: 12px;
}

.audio-level-text {
position: absolute;
right: 12px;
top: 50%;
transform: translateY(-50%);
font-size: 0.8125rem;
color: rgba(255, 255, 255, 0.85);
font-weight: 500;
}

/* Recording Tips */
.recording-tips {
margin-bottom: 1.25rem;
padding: 1rem;
background-color: rgba(255, 255, 255, 0.06);
border-radius: 0.5rem;
border: 1px solid rgba(255, 255, 255, 0.15);
}

.recording-tips h4 {
margin: 0 0 0.625rem 0;
color: rgba(255, 255, 255, 0.95);
font-size: 0.9375rem;
}

.recording-tips ul {
margin: 0;
padding-left: 1.75rem;
font-size: 0.875rem;
color: rgba(255, 255, 255, 0.8);
}

.recording-tips li {
margin-bottom: 0.375rem;
}

/* Quality Settings */
.quality-settings {
margin-bottom: 1rem;
}

.quality-select {
width: 100%;
padding: 0.625rem 0.75rem;
border: 1px solid rgba(255, 255, 255, 0.15);
border-radius: 0.5rem;
background-color: rgba(0, 0, 0, 0.6);
font-size: 0.9375rem;
color: rgba(255, 255, 255, 0.95);
cursor: pointer;
}

.quality-select:focus {
outline: none;
border-color: rgba(255, 255, 255, 0.3);
}

.quality-select option {
background-color: #1a202c;
color: white;
padding: 0.5rem;
}

/* Recording Controls */
.recorder-controls {
display: flex;
align-items: center;
gap: 1rem;
margin-bottom: 0.75rem;
}

.record-button {
flex: 1;
padding: 0.875rem 1.25rem;
border: none;
border-radius: 0.5rem;
background-color: #e53e3e;
color: white;
cursor: pointer;
transition: all 0.2s ease;
display: flex;
justify-content: center;
align-items: center;
font-size: 1rem;
font-weight: 500;
}

.record-button:hover:not(:disabled) {
background-color: #c53030;
transform: translateY(-1px);
}

.record-button:disabled {
opacity: 0.7;
cursor: not-allowed;
}

.record-button.is-recording {
animation: pulse 1.5s infinite;
}

.record-button.is-preparing {
background-color: #ed8936;
}

.button-content {
display: flex;
align-items: center;
gap: 0.625rem;
}

.record-icon {
font-size: 1.375rem;
}

.cancel-button {
padding: 0.875rem 1.5rem;
background-color: rgba(255, 255, 255, 0.15);
border: 1px solid rgba(255, 255, 255, 0.2);
border-radius: 0.5rem;
color: rgba(255, 255, 255, 0.9);
cursor: pointer;
transition: all 0.2s ease;
font-size: 0.9375rem;
font-weight: 500;
}

.cancel-button:hover {
background-color: rgba(255, 255, 255, 0.2);
border-color: rgba(255, 255, 255, 0.3);
color: white;
}

/* Countdown Overlay */
.countdown-overlay {
position: fixed;
top: 0;
left: 0;
right: 0;
bottom: 0;
background-color: rgba(0, 0, 0, 0.85);
display: flex;
align-items: center;
justify-content: center;
z-index: 1000;
}

.countdown-content {
background-color: rgba(26, 32, 44, 0.97);
color: white;
padding: 3.5rem;
border-radius: 1.25rem;
text-align: center;
border: 2px solid rgba(255, 255, 255, 0.15);
box-shadow: 0 0 40px rgba(0, 0, 0, 0.6);
}

.countdown-number {
font-size: 5.5rem;
font-weight: bold;
margin-bottom: 0.75rem;
text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
animation: countdownPulse 1s ease-in-out;
}

.countdown-text {
font-size: 1.5rem;
margin-bottom: 1.25rem;
color: rgba(255, 255, 255, 0.95);
}

.countdown-cancel {
color: rgba(255, 255, 255, 0.7);
cursor: pointer;
font-size: 0.9375rem;
padding: 0.625rem 1.25rem;
border: 1px solid rgba(255, 255, 255, 0.25);
border-radius: 0.5rem;
transition: all 0.2s ease;
display: inline-block;
}

.countdown-cancel:hover {
background-color: rgba(255, 255, 255, 0.15);
color: white;
border-color: rgba(255, 255, 255, 0.4);
}

@keyframes countdownPulse {
0% { transform: scale(0.8); opacity: 0; }
50% { transform: scale(1.1); }
100% { transform: scale(1); opacity: 1; }
}

/* Recording Actions */
.recording-actions {
display: flex;
gap: 0.75rem;
margin-top: 1rem;
margin-bottom: 1rem;
}

.action-button {
padding: 0.625rem 1rem;
border: none;
border-radius: 0.5rem;
color: white;
cursor: pointer;
transition: all 0.2s ease;
font-size: 0.875rem;
font-weight: 500;
display: inline-flex;
align-items: center;
justify-content: center;
gap: 0.375rem;
}

.action-button:hover:not(:disabled) {
opacity: 0.9;
transform: translateY(-1px);
}

.action-button:disabled {
opacity: 0.7;
cursor: not-allowed;
}

.action-button.large {
padding: 0.75rem 1.25rem;
font-size: 0.9375rem;
flex: 1;
}

.preview-button {
background-color: #4299e1;
}

.save-button {
background-color: #48bb78;
}

.delete-button {
background-color: #e53e3e;
}

.discard-button {
background-color: #718096;
}

.edit-button {
background-color: #805ad5;
}

/* Recording Indicator */
.recording-indicator {
display: flex;
align-items: center;
gap: 1rem;
margin-top: 1rem;
justify-content: center;
}

.record-wave {
display: flex;
align-items: center;
gap: 3px;
}

.wave-bar {
width: 4px;
height: 20px;
background-color: #e53e3e;
border-radius: 2px;
animation: soundBars 1.2s ease-in-out infinite;
}

.wave-bar:nth-child(1) { animation-delay: 0s; }
.wave-bar:nth-child(2) { animation-delay: 0.2s; }
.wave-bar:nth-child(3) { animation-delay: 0.4s; }
.wave-bar:nth-child(4) { animation-delay: 0.6s; }
.wave-bar:nth-child(5) { animation-delay: 0.8s; }

.recording-timer {
font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
color: rgba(255, 255, 255, 0.95);
font-weight: 600;
font-size: 1.125rem;
}

.recording-quality {
font-size: 0.8125rem;
color: rgba(255, 255, 255, 0.85);
background-color: rgba(255, 255, 255, 0.15);
padding: 0.375rem 0.75rem;
border-radius: 0.375rem;
font-weight: 500;
}

/* Recording Info */
.recording-info {
margin-top: 1rem;
display: flex;
flex-direction: column;
gap: 0.625rem;
padding: 1rem;
background-color: rgba(255, 255, 255, 0.06);
border-radius: 0.5rem;
border: 1px solid rgba(255, 255, 255, 0.1);
}

.recording-length,
.recording-size,
.recording-format {
font-size: 0.9375rem;
color: rgba(255, 255, 255, 0.85);
font-weight: 500;
}

/* Recording Warning */
.recording-warning {
margin-top: 0.75rem;
font-size: 0.9375rem;
color: #ed8936;
text-align: center;
font-weight: 500;
padding: 0.75rem;
background-color: rgba(237, 137, 54, 0.1);
border: 1px solid rgba(237, 137, 54, 0.3);
border-radius: 0.5rem;
}

.recording-waveform {
display: flex;
align-items: center;
gap: 2px;
height: 50px;
margin-top: 0.75rem;
padding: 0.5rem;
background-color: rgba(0, 0, 0, 0.2);
border-radius: 0.375rem;
}

.waveform-bar {
flex: 1;
background-color: #4299e1;
border-radius: 2px;
transition: height 0.2s ease;
}

/* Animations */
@keyframes pulse {
0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.4); }
50% { opacity: 0.9; box-shadow: 0 0 0 6px rgba(229, 62, 62, 0); }
}

@keyframes soundBars {
0%, 100% { transform: scaleY(0.3); }
50% { transform: scaleY(1); }
}

/* Media queries */
@media (max-width: 768px) {
.recorder-controls {
flex-direction: column;
gap: 0.75rem;
}

.record-button,
.cancel-button {
width: 100%;
}

.recording-actions {
flex-direction: column;
}

.action-button.large {
width: 100%;
}

.existing-recording-actions {
flex-direction: column;
}

.existing-recording-actions .action-button {
width: 100%;
}

.countdown-content {
padding: 2.5rem;
}

.countdown-number {
font-size: 4rem;
}

.voice-recorder {
padding: 0.625rem;
}
}
</style>
